import { useState } from 'react'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { getErrorFromCatch, serializeFieldErrors } from '../api/api'
import { IPageParams } from '../api/IApi'
import { IPatchIOrderItemInfo, IPatchOrderInfo, orderApi } from '../api/order.api'
import { TOrderSearchState } from '../components/orders/OrderList'
import { IOrder, IOrderItem } from '../model/IOrder'
import { msg } from '../msg'
import { IUrlParamsMap } from '../routing/IUrlParamsMap'
import checkFormStateByZod from '../utils/checkFormStateByZod'
import useSchemaSearchParams from './useSerarchParams'

export type ITrackingType = 'Multi-Order' | 'Whole Order' | ''
export interface IOrderItemsTrackings {
  id: number
  trackingNum: string
  carrier: string
}

export type TTrackingState = {
  carrier: string
  trackingNum: string
}
export type TPatchOrderItem = (
  orderId: IOrder['orderId'],
  id: number,
  field: Exclude<keyof IPatchIOrderItemInfo, 'id'>,
  value: string | number,
) => Promise<void>

export const initOrderSearchState: TOrderSearchState = {
  orderId: '',
  siEmail: '',
  invoice: '',
}
export const initOrderPageParam: IPageParams = {
  page: 0,
  size: 30,
  q: '',
  sort: 'timeCreate',
  sortDir: 'desc',
}

// --
// -- Form schemas
// --
const trackingInfoScheme = z.object({
  carrier: z
    .string({ required_error: msg('trackinginfo-no-carrier') })
    .min(2, msg('trackinginfo-short-carrier')),
  trackingNum: z
    .string({ required_error: msg('trackinginfo-no-tracking') })
    .min(2, msg('trackinginfo-short-tracking')),
} as Record<keyof TTrackingState, any>)

export default function useOrder() {
  const param = useSchemaSearchParams()

  const [loading, setLoading] = useState<boolean>(false)

  const getOrderSearchStateFromUrl = (): TOrderSearchState => ({
    ...initOrderSearchState,
    ...(param.get('orderSearchId') ? { orderId: param.get('orderSearchId') } : {}),
    ...(param.get('orderSearchSiEmail') ? { siEmail: param.get('orderSearchSiEmail') } : {}),
    ...(param.get('orderSearchInvoice') ? { invoice: param.get('orderSearchInvoice') } : {}),
  })

  const getOrderSearchPageParamFromUrl = (): IPageParams => ({
    ...initOrderPageParam,
    ...(Number(param.get('orderSearchPage')) ? { page: Number(param.get('orderSearchPage')) } : {}),
    ...(param.get('orderSearchSort') ? { sort: param.get('orderSearchSort') } : {}),
    ...(param.get('orderSearchSortDir')
      ? { sortDir: param.get('orderSearchSortDir') === 'desc' ? 'desc' : 'asc' }
      : {}),
  })

  const getPageParamsAppliedUrlString = (pageParams: IPageParams): string => {
    const urlParams: IUrlParamsMap = {
      orderSearchId: param.get('orderSearchId') || initOrderSearchState.orderId,
      orderSearchSiEmail: param.get('orderSearchSiEmail') || initOrderSearchState.siEmail,
      orderSearchInvoice: param.get('orderSearchInvoice') || initOrderSearchState.invoice,
      orderSearchPage: Number(pageParams?.page)
        ? String(pageParams?.page)
        : /* param.get('orderSearchPage') || */ String(initOrderPageParam.page),
      orderSearchSort:
        pageParams?.sort /* || param.get('orderSearchSort') */ || initOrderPageParam.sort,
      orderSearchSortDir:
        pageParams?.sortDir /* || param.get('orderSearchSortDir') */ || initOrderPageParam.sortDir,
    }

    return new URLSearchParams(urlParams).toString()
  }

  const getStateAppliedUrlString = (orderSearchState: TOrderSearchState): string =>
    new URLSearchParams({
      orderSearchId: orderSearchState?.orderId,
      orderSearchSiEmail: orderSearchState?.siEmail,
      orderSearchInvoice: orderSearchState?.invoice,
    }).toString()

  // --
  // -- return: null - OK, { error? } - Error
  // --
  const [tryToPatchOrder] = orderApi.usePatchOrderMutation()
  const patchOrder = async (order: IPatchOrderInfo): Promise<Record<string, string> | null> => {
    try {
      setLoading(() => true)
      await tryToPatchOrder(order).unwrap()
      return null
    } catch (err) {
      toast.error(`${msg('failed-to-add-tracking-info')} ${getErrorFromCatch(err)}`)
      return serializeFieldErrors(err) ?? { message: getErrorFromCatch(err) }
    } finally {
      setLoading(() => false)
    }
  }

  const patchAllProductsTrackings = async (
    data: TTrackingState,
    order: IOrder,
  ): Promise<Partial<Record<keyof TTrackingState, string>> | null> => {
    const trackingWholeOrderErrs = checkFormStateByZod(trackingInfoScheme, data)
    if (trackingWholeOrderErrs) return trackingWholeOrderErrs

    // -- Generate changings for all items
    const orderItems = order?.orderItems?.map(({ id }) => ({
      id,
      carrier: data?.carrier,
      trackingNum: data?.trackingNum,
    }))

    try {
      const errs = await patchOrder({
        orderId: order.orderId,
        orderItems,
      })
      if (errs?.message) throw new Error(errs?.message)
      return null
    } catch (err) {
      return { carrier: String(err) }
    }
  }

  const patchAllProductTrackings = async (
    data: TTrackingState,
    orderItem: IOrderItem,
  ): Promise<Partial<Record<keyof TTrackingState, string>> | null> => {
    const trackingWholeOrderErrs = checkFormStateByZod(trackingInfoScheme, data)
    if (trackingWholeOrderErrs) return trackingWholeOrderErrs

    try {
      const errs = await patchOrder({
        orderId: orderItem.orderId,
        orderItems: [
          {
            id: orderItem.id,
            carrier: data?.carrier,
            trackingNum: data?.trackingNum,
          },
        ],
      })
      if (errs?.message) throw new Error(errs?.message)
      return null
    } catch (err) {
      return { carrier: String(err) }
    }
  }

  const patchOrderItem: TPatchOrderItem = async (orderId, id, field, value) => {
    await patchOrder({ orderId, orderItems: [{ id, [field]: value }] })
  }

  return {
    getOrderSearchStateFromUrl, // search state
    getStateAppliedUrlString,
    getOrderSearchPageParamFromUrl,
    getPageParamsAppliedUrlString,

    patchOrder,
    patchOrderItem,
    patchAllProductsTrackings,
    patchAllProductTrackings,
    loading,
  }
}
